import type { QueryClient } from '@tanstack/react-query';

import { broadcastDashboardMutation } from '@/features/dashboard/api/useDashboardSync';
import type { DashboardTab } from '@/features/dashboard/types';
import { http } from '@/services/http';

import {
  fetchDashboardConfig,
  isDashboardNotFoundError,
  type DashboardConfig
} from '../dashboard.api';
import { dashboardOptions } from '../dashboard.options';
import {
  matchesDashboardExpectation,
  waitForDashboardConsistency,
  type DashboardConsistencyExpectation
} from '../dashboardReconciliation';
import { getDashboardApiUrl } from '../dashboardUrl';
import { isRenameOperation } from './dashboardMutation.utils';

export const saveDashboardToApi = async (
  dashboardName: string,
  payload: DashboardTab,
  oldDashboardName?: string
): Promise<DashboardConfig> => {
  const isRename = isRenameOperation(oldDashboardName, dashboardName);
  const expectation: DashboardConsistencyExpectation = {
    type: 'upsert',
    dashboardName,
    dashboard: payload,
    ...(isRename && oldDashboardName ? { oldDashboardName } : {})
  };

  if (isRename && oldDashboardName) {
    const dashboards = await fetchDashboardConfig();
    if (matchesDashboardExpectation(dashboards, expectation)) return dashboards;

    try {
      await http.internal.patch(getDashboardApiUrl(encodeURIComponent(oldDashboardName)), payload, {
        params: { newKey: dashboardName }
      });
    } catch (error) {
      if (!isDashboardNotFoundError(error)) throw error;
    }
  } else {
    await http.internal.post(getDashboardApiUrl(encodeURIComponent(dashboardName)), payload);
  }

  return waitForDashboardConsistency(expectation);
};

export const saveDashboardsBulkToApi = async (
  fullStructure: DashboardConfig
): Promise<DashboardConfig> => {
  await http.internal.post(getDashboardApiUrl(), fullStructure);
  return waitForDashboardConsistency({ type: 'replace', dashboards: fullStructure });
};

export const deleteDashboardFromApi = async (
  dashboardName: string,
  retainedDashboards: DashboardConfig
): Promise<DashboardConfig> => {
  const expectation: DashboardConsistencyExpectation = {
    type: 'delete',
    dashboardName,
    retainedDashboards
  };

  try {
    await http.internal.delete(getDashboardApiUrl(encodeURIComponent(dashboardName)));
  } catch (error) {
    if (!isDashboardNotFoundError(error)) throw error;
  }

  return waitForDashboardConsistency(expectation);
};

export const commitConfirmedDashboardConfig = (
  queryClient: QueryClient,
  dashboards: DashboardConfig
): void => {
  queryClient.setQueryData(dashboardOptions.all(), dashboards);
  broadcastDashboardMutation(dashboards);
};
